import {
  HTMLAttributes,
  Key,
  MouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ClickAwayListener, Popper, styled, Typography } from '@mui/material';
import maxSize from 'popper-max-size-modifier';

import { SxProps } from '../../types';
import { tokens } from '../../theme/generated/tokens';
import {
  CriteriaSearch,
  SearchInCriteriaProps,
} from './_components/CriteriaSearch';
import { Options, OptionsListProps } from './_components/Options';
import { RecentSearch, RecentSearchProps } from './_components/RecentSearch';
import { Criterion } from './types';
import { dropdownOptionsSort } from './utils';
import { mergeSx } from '../../utils';
import {
  AutocompleteOverrides,
  ChipInputForwardRef,
  DropdownProps,
  InputChipProps,
  useDropdownWithAutocomplete,
} from '../controls';
import { ChipInput, ChipInputProps } from '../controls/chip-input';
import SearchChip from './_components/SearchChip';

const Root = styled('div', {
  name: 'DsSearch',
  slot: 'Root',
  target: 'DsSearch-Root',
  overridesResolver: (_, styles) => [styles.root],
})``;

const NotFound = styled('div', {
  name: 'DsSearch',
  slot: 'NotFound',
  target: 'DsSearch-NotFound',
  overridesResolver: (_, styles) => [styles.notFound],
})<HTMLAttributes<HTMLDivElement>>``;

type SearchProps<
  OptionItem,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
> = {
  rootSx?: SxProps;
  inputChipSx?: SxProps;
  listHeight?: number;
  searchWhileActive?: boolean;
  disableFreeText?: boolean;
  disabled?: boolean;
  chipInputProps?: Omit<
    ChipInputProps<InputChipProps>,
    'onChange' | 'onBlur' | 'value'
  >;
  dropdownProps: DropdownProps<OptionItem, OptionValue, SelectionType>;
  autocompleteProps: Partial<
    AutocompleteOverrides<OptionItem, OptionValue, SelectionType>
  >;
  recentSearchProps?: RecentSearchProps;
  criteriaSearchProps?: SearchInCriteriaProps;
  activeCriterion?: Criterion;
  activeCriterionValue?: any;
  clearCriteria?: (key?: Key) => void;
  editCriteria?: (key?: Key) => void;
  testId?: string;
  onPopperClose?: () => void;
  disableNoOptionsFoundWarning?: boolean;
  onOptionsClose?: () => void;
} & Pick<
  OptionsListProps<OptionItem, OptionValue>,
  'selectedOptions' | 'toggleOption'
>;

export const Search = <
  OptionItem,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
>({
  rootSx,
  listHeight = 500,
  searchWhileActive = false,
  disableFreeText = false,
  disabled = false,
  chipInputProps = {},
  recentSearchProps,
  criteriaSearchProps,
  dropdownProps,
  autocompleteProps,
  activeCriterion,
  clearCriteria,
  testId,
  inputChipSx,
  onPopperClose,
  editCriteria,
  activeCriterionValue,
  onOptionsClose,
  ...props
}: SearchProps<OptionItem, OptionValue, SelectionType>) => {
  const chipInputRef = useRef<ChipInputForwardRef>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [maxListHeight, setMaxListHeight] = useState(listHeight);

  const autocomplete = useDropdownWithAutocomplete({
    dropdownProps,
    autocompleteOverrides: {
      applySortingWhileSearching: true,
      resetInputOnClose: false,
      ...autocompleteProps,
    },
  });
  const inputPropsFromAutocomplete = autocomplete.getters.getInputProps();
  autocomplete.inputRef.current = chipInputRef.current?.inputRef ?? null;

  const showNotFound =
    !autocomplete.states.options.length &&
    !searchWhileActive &&
    !!activeCriterion?.key;

  const showOptions =
    !!activeCriterion?.key &&
    !showNotFound &&
    !!autocomplete.states.options.length;

  const showCriteriaSelection =
    !!criteriaSearchProps?.criteria?.length &&
    !showOptions &&
    !activeCriterion?.key;

  const showRecentSearch =
    !activeCriterion?.key &&
    !showOptions &&
    (!!recentSearchProps?.recentSearches?.length ||
      !!criteriaSearchProps?.criteria?.length);

  const showPopper =
    !disabled &&
    !!anchorEl &&
    (showOptions || showCriteriaSelection || showRecentSearch || showNotFound);

  const closePopper = useCallback(() => {
    if (showOptions && activeCriterion.allowMultiple) {
      onOptionsClose?.();
    }
    setAnchorEl(null);
    onPopperClose?.();
  }, [onPopperClose, onOptionsClose, showOptions, activeCriterion]);

  const openPopper = useCallback(() => {
    if (anchorRef.current && !showPopper && !chipInputProps?.readOnly) {
      setAnchorEl(anchorRef.current);
    }
  }, [showPopper, chipInputProps?.readOnly]);

  const chips = useMemo(() => {
    return chipInputProps?.chips?.map((chipProps) => ({
      ...chipProps,
      onDestroy: (e: MouseEvent) => {
        if (disabled) {
          return;
        }
        e.stopPropagation();
        clearCriteria?.(chipProps.key);
      },
      onClick: () => {
        if (disabled || chipInputProps?.readOnly) {
          return;
        }
        editCriteria?.(chipProps.key);
        chipInputRef.current?.inputRef?.focus();
      },
      sx: inputChipSx,
      testId: chipProps.testId ?? 'selected-criteria-chip',
    }));
  }, [
    chipInputProps?.chips,
    clearCriteria,
    inputChipSx,
    editCriteria,
    disabled,
    chipInputProps?.readOnly,
  ]);

  const hideInput =
    disableFreeText &&
    !!anchorEl &&
    !showOptions &&
    !activeCriterion &&
    !chipInputProps?.chips?.length;

  const noFreeText = disableFreeText && !activeCriterion?.key;

  const options = useMemo(() => {
    return dropdownOptionsSort(
      autocomplete.states.options,
      Array.isArray(activeCriterionValue) ? activeCriterionValue : undefined
    );
  }, [autocomplete.states.options, activeCriterionValue]);

  return (
    <ClickAwayListener mouseEvent="onMouseDown" onClickAway={closePopper}>
      <Root ref={anchorRef} sx={rootSx} data-testid={testId}>
        <ChipInput
          testId="search-chip-input"
          {...chipInputProps}
          ChipComponent={SearchChip}
          chips={chips}
          inputProps={{
            ...inputPropsFromAutocomplete,
            ...chipInputProps?.inputProps,
            onChange: noFreeText
              ? undefined
              : inputPropsFromAutocomplete.onChange,
            onKeyDown: (event) => {
              const valueIsEmpty =
                (event.target as HTMLInputElement).value === '';
              if (
                event.key === 'Backspace' &&
                activeCriterion &&
                valueIsEmpty
              ) {
                clearCriteria?.(activeCriterion.key);
              }
              if (event.key === 'Escape') {
                if (activeCriterion) {
                  onOptionsClose?.();
                  clearCriteria?.(activeCriterion.key);
                } else {
                  closePopper();
                }
                event.stopPropagation();
              }
            },
          }}
          value={inputPropsFromAutocomplete.value}
          onClick={openPopper}
          sx={mergeSx(
            hideInput && {
              opacity: '0',
            },
            noFreeText && {
              '& input': {
                caretColor: 'transparent',
              },
            },
            chipInputProps?.sx
          )}
          disabled={disabled}
          ref={chipInputRef}
        />
        <Popper
          open={showPopper}
          anchorEl={anchorEl}
          sx={(theme) => ({
            boxShadow: tokens.theme.shadows.elevation.medium,
            padding: '4px 0',
            background: tokens.theme.bg.structural.surface,
            borderRadius: tokens.theme.radius.sm,
            display: 'flex',
            flexDirection: 'column',
            zIndex: theme.zIndex.drawer + 1,
            width: anchorRef.current
              ? anchorRef.current.getBoundingClientRect().width
              : undefined,
          })}
          modifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: 'viewport',
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: 'viewport',
              },
            },
            {
              name: 'offset',
              enabled: !hideInput,
              options: {
                offset: [0, 4],
              },
            },
            {
              name: 'offset',
              enabled: hideInput,
              options: {
                offset: ({ reference }: any) => {
                  return [0, -reference.height];
                },
              },
            },
            maxSize,
            {
              name: 'applyMaxSize',
              enabled: true,
              phase: 'beforeWrite',
              requires: ['maxSize'],
              fn({ state }) {
                // The `maxSize` modifier provides this data
                const { height } = state.modifiersData.maxSize;

                const maxHeight = Math.min(listHeight, height - 8); // 8px is padding from the viewport edge
                setMaxListHeight(maxHeight - 8); // 8px is the padding of the popper

                state.styles.popper = {
                  ...state.styles.popper,
                  maxHeight: `${maxHeight}px`,
                };
              },
            },
          ]}
          placement="bottom-start"
          componentsProps={{
            root: { 'data-testid': 'search-chip-popper' } as any,
          }}
        >
          {showCriteriaSelection && <CriteriaSearch {...criteriaSearchProps} />}
          {showRecentSearch && (
            <RecentSearch
              {...recentSearchProps}
              onSelect={(index) => {
                recentSearchProps?.onSelect?.(index);
                closePopper();
              }}
            />
          )}
          {showOptions && (
            <Options
              listHeight={maxListHeight}
              activeCriterion={activeCriterion}
              options={options}
              getOptionProps={autocomplete.getters.getOptionProps}
              selectedOptions={props.selectedOptions}
              toggleOption={props.toggleOption}
              showOptions={showOptions && !showNotFound}
            />
          )}
          {showNotFound && (
            <NotFound>
              <Typography
                variant="P14M"
                sx={{ color: tokens.theme.fg.structural.primary }}
                px="16px"
              >
                No results found
              </Typography>
              <Typography
                variant="P12R"
                sx={{ color: tokens.theme.fg.structural.secondary }}
                px="16px"
              >
                Unfortunately, we did not find any criteria matching “
                {inputPropsFromAutocomplete.value}”.
              </Typography>
            </NotFound>
          )}
        </Popper>
      </Root>
    </ClickAwayListener>
  );
};
